/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

// tslint:disable:no-non-null-assertion
// tslint:disable:no-magic-numbers

import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { DialogService, WaitingService } from '@gh/core-ui';
import { filter$ } from '@gh/rx/operators';
import { Point } from 'paper';
import { GraphicalTakeOffCommandService } from '../../graphical-take-off-command.service';
import { GraphicalTakeOffFormState } from '../../graphical-take-off-form.service';
import { GraphicalTakeOffBusinessService, GraphicalTakeOffHousePlanView } from '../../resources';
import { fromPaperPoint } from '../canvas/canvas.utils';
import { graphicalTakeOffZoomDialogRoute } from '../zoom';

/**
 * Draw component
 */
@Component({
  selector: 'gh-graphical-take-off-draw',
  template: require('./graphical-take-off-draw.component.html'),
  styles: [require('./graphical-take-off-draw.component.scss')],
})
export class GraphicalTakeOffDrawComponent {
  /**
   * Entry point
   *
   * @type {string}
   */
  @Input() entryPoint: string;

  /**
   * Level mode
   *
   * @type {boolean}
   */
  @Input() levelMode: boolean = false;

  /**
   * Crop mode
   *
   * @type {boolean}
   */
  @Input() cropMode: boolean = false;

  /**
   * Active view
   *
   * @type {GraphicalTakeOffHousePlanView}
   */

  @Input() allowEdit: boolean = false;

  /**
   * Import event emitter
   *
   * @type {EventEmitter<boolean>}
   */
    // tslint:disable-next-line:no-output-on-prefix
  @Output() onImport: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Scale mode event emitter
   *
   * @type {EventEmitter<boolean>}
   */
    // tslint:disable-next-line:no-output-on-prefix
  @Output() startScaling: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * On status change
   *
   * @type {EventEmitter<string>}
   */
    // tslint:disable-next-line:no-output-on-prefix
  @Output() onStatus: EventEmitter<string> = new EventEmitter();

  /**
   * On clone
   *
   * @type {EventEmitter<boolean>}
   */
    // tslint:disable-next-line:no-output-on-prefix
  @Output() onClone: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Constructor
   *
   * @param {any} data - Dialog data
   * @param {DialogService} dialogService - Dialog service
   * @param {WaitingService} waitingService - Waiting service
   * @param {GraphicalTakeOffBusinessService} gtoService GTO service
   */
  constructor(
    private dialogService: DialogService,
    private waitingService: WaitingService,
    private gtoService: GraphicalTakeOffBusinessService,
    public form: GraphicalTakeOffFormState,
    private commands: GraphicalTakeOffCommandService,
  ) {
  }

  get view(): GraphicalTakeOffHousePlanView {
    return this.form.currentView;
  }

  /**
   * Is import menu disabled?
   *
   * @returns {boolean} - Is import menu disabled?
   */
  get importDisabled(): boolean {
    return this.levelMode || this.cropMode || this.form.isSetScaleToolActive || !this.allowEdit;
  }

  /**
   * Is new area menu disabled?
   *
   * @returns {boolean} - Is new area menu disabled?
   */
  get newAreaDisabled(): boolean {
    if (this.scaleDisabled || this.levelMode || this.cropMode || this.form.isSetScaleToolActive || !this.allowEdit) {
      return true;
    }

    return !this.form.canStartNewArea;
  }

  /**
   * Is undo menu disabled?
   *
   * @returns {boolean} - Is undo menu disabled?
   */
  get undoDisabled(): boolean {
    return !this.allowEdit || this.cropMode || this.levelMode || this.form.isSetScaleToolActive || !this.form.canUndo;
  }

  /**
   * Is redo menu disabled?
   *
   * @returns {boolean} - Is undo redo disabled?
   */
  get redoDisabled(): boolean {
    return !this.allowEdit || this.cropMode || this.levelMode || this.form.isSetScaleToolActive || !this.form.canRedo;
  }

  /**
   * Is snap to start menu disabled?
   *
   * @returns {boolean} - Is snap to start menu disabled?
   */
  get snapToStartDisabled(): boolean {
    return !this.form.canSnapToStart || this.form.isSetScaleToolActive;
  }

  /**
   * Is clone menu disabled?
   *
   * @returns {boolean} - Is clone menu disabled?
   */
  get cloneDisabled(): boolean {
    return !this.form.canCloneSelectedTop || !this.allowEdit || this.form.isSetScaleToolActive;
  }

  /**
   * Is scale disabled?
   *
   * @returns {boolean}
   */
  get scaleDisabled(): boolean {
    if (!this.view) {
      return true;
    }

    const { picture, isScaled } = this.view;
    if (!picture || !isScaled) {
      return true;
    }

    return !this.allowEdit || this.form.isSetScaleToolActive;
  }

  get canDelete(): boolean {
    return this.allowEdit && !this.form.isSetScaleToolActive && this.form.canDeleteSelectedMeasurement;
  }

  /**
   * Import
   *
   * @returns {void}
   */
  import(): void {
    this.onImport.emit(true);
  }

  /**
   * Set scale mode
   *
   * @returns {void}
   */
  setScale(): void {
    this.startScaling.emit(true);
  }

  /**
   * On clone event
   *
   * @returns {void}
   */
  clone(): void {
    this.onClone.emit(true);
  }

  /**
   * Init new measurement
   *
   * @returns {void}
   */
  newArea(): void {
    this.commands.switchOnTopAppendMode();
  }

  /**
   * Snap to start measurement
   *
   * @returns {void}
   */
  snapToStart(): void {
    this.commands.snapToStart();
  }

  /**
   * Undo step
   *
   * @returns {void}
   */
  undo(): void {
    this.commands.undo();
  }

  /**
   * Redo step
   *
   * @returns {void}
   */
  redo(): void {
    this.commands.redo();
  }

  onDelete(): void {
    this.commands.deleteSelectedMeasurement();
  }
}
